import { RefreshCw } from 'lucide-react';
import { Button, Card } from '@/components/ui';
import { parseLocalDate } from '@/lib/date';
import { useAdminMetrics } from './hooks/useAdminMetrics';
import { KPICard } from './components/KPICard';
import { FunnelStep } from './components/FunnelStep';
import { TrendChart } from './components/TrendChart';
import { DistributionChart } from './components/DistributionChart';
import { DonutChart } from './components/DonutChart';
import { QueueHealth } from './components/QueueHealth';

// Chart axis labels. Uses parseLocalDate so the date-only strings the SQL
// views return ("2026-04-22") render as the intended day — not day-1 west of
// UTC, which is the native-`new Date()` trap.
function shortDate(iso: string): string {
  const d = parseLocalDate(iso);
  if (Number.isNaN(d.getTime())) return iso;
  return d.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
}

interface AdminMetricsProps {
  password: string;
}

export function AdminMetrics({ password }: AdminMetricsProps) {
  const { status, data, error, lastFetchedAt, refetch } =
    useAdminMetrics(password);

  if (status === 'loading' && !data) {
    return <div className="text-muted-foreground">Loading metrics…</div>;
  }
  if (status === 'error' && !data) {
    return (
      <Card className="p-4 border-danger">
        <div className="text-sm text-danger">
          Failed to load metrics: {error}
        </div>
        <Button
          variant="secondary"
          onClick={refetch}
          className="mt-3"
        >
          Retry
        </Button>
      </Card>
    );
  }
  if (!data) return null;

  const { overview, activation, engagement, billing } = data;
  const onboardingPct =
    overview.total_users > 0
      ? (overview.users_onboarded / overview.total_users) * 100
      : 0;

  return (
    <div className="flex flex-col gap-8">
      {/* Header: last refresh + manual refresh */}
      <div className="flex items-center justify-between">
        <div className="text-xs text-muted-foreground">
          Last refreshed:{' '}
          {lastFetchedAt ? lastFetchedAt.toLocaleTimeString() : '—'}
        </div>
        <Button
          variant="secondary"
          onClick={refetch}
          disabled={status === 'loading'}
        >
          <RefreshCw
            className={`h-4 w-4 mr-2 ${status === 'loading' ? 'animate-spin' : ''}`}
          />
          Refresh
        </Button>
      </div>

      {/* Overview KPIs */}
      <section className="flex flex-col gap-3">
        <h2 className="text-lg font-semibold">Overview</h2>
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-5 gap-3">
          <KPICard
            label="Total users"
            value={overview.total_users}
          />
          <KPICard
            label="New (7d)"
            value={overview.total_users_7d}
            hint="signups in last 7 days"
          />
          <KPICard
            label="New (30d)"
            value={overview.total_users_30d}
            hint="signups in last 30 days"
          />
          <KPICard
            label="Onboarding"
            value={`${onboardingPct.toFixed(1)}%`}
            hint={`${overview.users_onboarded} of ${overview.total_users} completed`}
          />
          <KPICard
            label="Paid users"
            value={overview.paid_users}
            hint="Core + Family subscribers"
          />
        </div>
      </section>

      {/* Activation funnel */}
      <section className="flex flex-col gap-3">
        <h2 className="text-lg font-semibold">Activation funnel</h2>
        <Card className="p-4 flex flex-col gap-4">
          <FunnelStep
            label="Signed up"
            count={activation.funnel.signup}
            totalForPercent={activation.funnel.signup}
          />
          <FunnelStep
            label="Completed onboarding"
            count={activation.funnel.onboarding_complete}
            totalForPercent={activation.funnel.signup}
          />
          <FunnelStep
            label="Added a child"
            count={activation.funnel.added_child}
            totalForPercent={activation.funnel.signup}
          />
          <FunnelStep
            label="First email forwarded"
            count={activation.funnel.first_email}
            totalForPercent={activation.funnel.signup}
          />
          <FunnelStep
            label="First task or event"
            count={activation.funnel.first_task_or_event}
            totalForPercent={activation.funnel.signup}
            isLast
          />
        </Card>
        <Card className="p-4">
          <div className="text-sm font-medium mb-2">
            Signups per day (last 30)
          </div>
          <TrendChart
            data={activation.signups_by_day.map((d) => ({
              label: d.day,
              count: d.count,
            }))}
            xTickFormatter={shortDate}
          />
        </Card>
      </section>

      {/* Engagement */}
      <section className="flex flex-col gap-3">
        <h2 className="text-lg font-semibold">Engagement</h2>
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-3">
          <Card className="p-4">
            <div className="text-sm font-medium mb-2">Emails / week</div>
            <TrendChart
              data={engagement.emails_by_week.map((d) => ({
                label: d.week,
                count: d.count,
              }))}
              xTickFormatter={shortDate}
            />
            <div className="text-xs text-muted-foreground mt-2">
              Last 12 weeks, excluding REJECTED
            </div>
          </Card>
          <Card className="p-4">
            <div className="text-sm font-medium mb-2">Tasks / week</div>
            <TrendChart
              data={engagement.tasks_by_week.map((d) => ({
                label: d.week,
                count: d.count,
              }))}
              xTickFormatter={shortDate}
            />
          </Card>
          <Card className="p-4">
            <div className="text-sm font-medium mb-2">Events / week</div>
            <TrendChart
              data={engagement.events_by_week.map((d) => ({
                label: d.week,
                count: d.count,
              }))}
              xTickFormatter={shortDate}
            />
          </Card>
        </div>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
          <Card className="p-4 flex flex-col gap-2">
            <div className="text-sm font-medium">Review outcomes</div>
            <DonutChart
              data={[
                {
                  label: 'Committed',
                  count: engagement.review_outcomes.committed,
                },
                {
                  label: 'Dismissed',
                  count: engagement.review_outcomes.dismissed,
                },
                { label: 'Pending', count: engagement.review_outcomes.pending },
              ]}
              emptyHint="No review items yet"
            />
          </Card>
          <Card className="p-4 flex flex-col gap-2">
            <div className="text-sm font-medium">Invitations</div>
            <div className="text-sm text-muted-foreground">
              Sent:{' '}
              <strong className="text-foreground">
                {engagement.invitations_total}
              </strong>
              {' · '}
              Accepted:{' '}
              <strong className="text-foreground">
                {engagement.invitations_accepted}
              </strong>
              {engagement.invitations_total > 0 ? (
                <>
                  {' · '}
                  Accept rate:{' '}
                  <strong className="text-foreground">
                    {(
                      (engagement.invitations_accepted /
                        engagement.invitations_total) *
                      100
                    ).toFixed(1)}
                    %
                  </strong>
                </>
              ) : null}
            </div>
          </Card>
        </div>
      </section>

      {/* Billing */}
      <section className="flex flex-col gap-3">
        <h2 className="text-lg font-semibold">Billing</h2>
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-3">
          <Card className="p-4 flex flex-col gap-2">
            <div className="text-sm font-medium">Plan distribution</div>
            <DonutChart
              data={billing.plan_distribution.map((p) => ({
                label: p.tier,
                count: p.count,
              }))}
              emptyHint="No subscriptions yet"
            />
          </Card>
          <Card className="p-4 flex flex-col gap-2">
            <div className="text-sm font-medium">
              Email usage this month (per household)
            </div>
            <DistributionChart
              data={billing.email_usage_buckets.map((b) => ({
                label: b.bucket,
                count: b.count,
              }))}
              emptyHint="No usage this month"
            />
          </Card>
        </div>
      </section>

      {/*
        Queue health owns its own fetch, loading and error state — pgmq can be
        unreachable while every product metric above is fine.
      */}
      <QueueHealth password={password} />
    </div>
  );
}
